import React, { useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { ConceptChip } from '../common';

/**
 * ConceptFilterInput — **검색 조건**용 개념 고르개. 글자를 치면 후보가 뜨고, 고르면 칩이 된다.
 * 고른 칩은 **이 입력 상자 안에** 쌓인다(오너 지시 2026-08-29).
 *
 * ── 입력 줄이 맨 위에 고정이다 (한 번 무너뜨려 보고 정한 배치) ──────────────
 * 처음에는 흔한 토큰 입력처럼 칩을 먼저 흘리고 그 꼬리에 입력을 두었다. 좁은 열에서 무너졌다:
 * 화면 1280 에서 이 상자의 글자 자리가 194.6px 인데 개념 칩 하나(「삼각함수의 덧셈정리」)가
 * 그보다 넓어 **칩이 한 줄에 하나씩** 쌓였고, 두 개만 골라도 입력이 상자의 스크롤 밖으로
 * 밀려 **더 고를 칸이 화면에서 사라졌다.**
 *
 * 그래서 상자를 세로 두 칸으로 나눴다 — **위 칸은 늘 입력**(돋보기·글자·OR/AND), 아래 칸에만
 * 칩이 흐르고 거기만 스크롤한다. 칩을 아무리 골라도 입력 줄은 제자리에 남는다.
 * 아래 칸에 상한(max-h-12 ≈ 두 줄)을 둔 것은 옆에 선 버튼 기둥 때문이다 — 기둥이 조건 3행을
 * 걸치는 구조라, 이 상자가 자라면 초기화·검색 버튼이 함께 길어진다.
 *
 * ── 왜 ProblemHubV2/ConceptPicker 를 그대로 쓰지 않았는가 ──────────────────
 * 그 부품은 **개념 사전을 스스로 부른다.** 이 부품을 만들 때(2026-08-29) 그쪽은 v1
 * (`/api/mathcepts/temp`)이었고, ExamPapersV2 는 2026-08-26 에 v2(`/api/v2/mathcepts`)로 갈아탄
 * 뒤라 「v1 을 여기에 끌어오지 마라 — 넣는 순간 이 화면이 v2 전용이 아니게 된다」는 오너 방침
 * (conceptBadge.jsx 의 사전 주석)에 걸렸다. 그래서 **사전은 부르지 않고 받는다** — 이 부품은
 * API 를 모르고, 세대는 부품 밖에서 정한다. 2026-09-01 에 ConceptPicker 도 v2 로 갈아탔지만
 * 「부르는 부품 / 받는 부품」의 분리는 그대로 둔다 — 이 화면은 사전을 패널이 한 번 받아 셋이 나눠 쓴다.
 */
const MAX_MATCHES = 30; // ConceptPicker 와 같은 상한 — 같은 조작에 다른 수를 발명하지 않는다

/** 사전에서 id → 항목. 사전이 없으면 빈 지도(칩은 `#412` 로 선다) */
function useDictIndex(dict) {
  return useMemo(() => {
    const m = new Map();
    (dict || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [dict]);
}

/**
 * @param {number[]} value      고른 개념 id — 중복으로 고르지 않게 후보에서 빼는 데만 쓴다
 * @param {function} onChange   (ids:number[]) => void
 * @param {Array|null} dict     v2 개념 사전(fetchConceptDictV2 결과). null = 못 받음
 * @param {boolean} loading     사전을 읽는 중
 * @param {function} onRetry    사전 다시 읽기 (없으면 다시 시도 줄을 그리지 않는다)
 * @param {function} onSubmit   빈 입력에서 Enter — 보통 검색 실행
 * @param {string} placeholder  안내 글. 기본값은 「(비우면 전체)」까지 붙는 긴 쪽인데, 좁은 열에
 *                              세울 때는 짧은 것을 준다 — 실측(2026-08-29): 문제(/problem-hub)
 *                              화면의 좁은 마스터(안쪽 312px)에서 이 칸의 글자 자리가
 *                              130.9px 뿐이라 긴 문구(163.3px)가 잘린다.
 * @param {node} trailing       입력 상자 **안쪽** 오른쪽에 붙는 것(고르개 등). 상자를 flex 로
 *                              짠 이유가 이것이다 — 겹쳐 놓으면(absolute) 글자가 그 밑으로 흘러
 *                              들어가지 않게 오른쪽 여백을 손으로 맞춰야 하고, 그 값이 글꼴에
 *                              따라 어긋난다. 나란히 세우면 폭이 저절로 맞는다.
 */
function ConceptFilterInput({
  value = [],
  onChange,
  dict = null,
  loading = false,
  onRetry = null,
  onSubmit = null,
  placeholder = '개념명으로 검색(비우면 전체)',
  trailing = null,
}) {
  const [query, setQuery] = useState('');
  const byId = useDictIndex(dict);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  /**
   * 후보. **이름이 질의로 시작하는 것을 앞에 세운다** — 「미분」을 치면 「미분」이
   * 「음함수의 미분법」보다 먼저 와야 한다. 안 그러면 사전 순서(교육과정 순번)가 그대로 나와서
   * 찾던 그 개념이 목록 아래에 묻힌다.
   */
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '' || !Array.isArray(dict)) return [];
    const picked = new Set(value.map(String));
    const hit = dict.filter((c) => {
      if (picked.has(String(c.id))) return false;
      const name = String(c.name || c.concept_name || '').toLowerCase();
      return name.includes(q) || String(c.order ?? '').toLowerCase() === q || String(c.id) === q;
    });
    hit.sort((a, b) => {
      const an = String(a.name || a.concept_name || '').toLowerCase().startsWith(q) ? 0 : 1;
      const bn = String(b.name || b.concept_name || '').toLowerCase().startsWith(q) ? 0 : 1;
      return an - bn;
    });
    return hit.slice(0, MAX_MATCHES);
  }, [dict, query, value]);

  const add = (id) => {
    if (!value.includes(id)) onChange([...value, id]);
    setQuery('');
    inputRef.current?.focus(); // 연달아 고르는 조작이다 — 한 번 고를 때마다 다시 누르게 하지 않는다
  };

  const onKeyDown = (e) => {
    // 한글 조합 중의 Enter 는 「글자를 확정하는 Enter」다 — 여기서 가로채면 마지막 글자가 먹힌다
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim() !== '' && matches.length > 0) add(matches[0].id);
      else if (query.trim() === '' && typeof onSubmit === 'function') onSubmit();
      return;
    }
    // 빈 입력에서의 지우기는 마지막 칩을 지운다 — 칩마다 X 로 가는 것보다 손이 덜 간다
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
    // **ESC 는 잡지 않는다.** 이 부품이 서는 창(PaperComposeModal)은 ESC 의 임자를 위에서부터
    // 못박아 두었다 — 전체화면 미리보기 > 문제 미리보기 > 교체 모드 > 닫기 확인 > 모달 닫기.
    // 여기서 가로채면 그 사슬이 부품 안에 하나 더 생기고, 막다가 흘리면 「검색어만 지우려다
    // 시험지 편집 창이 닫히는」 일이 난다. 후보는 자리를 뜨면(blur) 닫히고 입력은 지우기로 비운다.
  };

  const open = focused && query.trim() !== '';

  /**
   * 후보가 열려 있는 동안 **이 상자를 형제(라벨·문제 ID 행)보다 위 층에 올린다.**
   * 후보 목록 자신에게 층을 주는 것만으로는 부족했다 — 실측(2026-08-29 · 빌드된 CSS 를 그대로
   * 물린 격리 하네스에서 겹치는 점을 elementsFromPoint 로 찍음): 목록이 `absolute z-20` 인데도
   * 히트 순서가 「라벨 input → 후보 button → 목록 div」로, **뒤따르는 입력이 목록 위에 그려졌다.**
   * 상자째 올리면 순서가 뒤집힌다(같은 자로 재서 확인). 닫혀 있을 때는 층을 만들지 않는다 —
   * 필요 없는 층은 나중에 다른 겹침을 낳는다.
   */
  return (
    <div className={`relative min-w-0 flex-1 ${open ? 'z-30' : ''}`}>
      {/* 상자에 세로 여백을 주지 않는다 — 위 칸의 input(py-1.5)이 높이를 만들어, 고른 게
          없을 때 이 상자가 다른 조건 칸과 같은 34px 로 선다. 아래 칸은 자기 몫만 더한다. */}
      <div className="rounded-lg border bg-white px-2 focus-within:border-primary">
        <div className="flex items-center gap-1">
          <Search className="size-3.5 shrink-0 text-gray-300" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            disabled={loading}
            aria-label="개념명 검색"
            placeholder={loading ? '개념 사전을 읽는 중…' : placeholder}
            className="min-w-0 flex-1 border-0 bg-transparent px-1 py-1.5 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
          {trailing}
        </div>
        {value.length > 0 && (
          // 아래 여백은 **스크롤 상자 밖**에 둔다 — 안에 두면 그 6px 이 상한(48px)을 잡아먹어
          // 칩 두 줄(22+4+22=48)이 딱 안 들어가고, 두 개만 골라도 스크롤이 생긴다(실측 2026-08-29).
          <div className="pb-1.5">
            <div className="flex max-h-12 flex-wrap items-center gap-1 overflow-y-auto">
              {value.map((cid) => (
                <ConceptChip
                  key={cid}
                  concept={byId.get(String(cid)) || { id: cid, text: `#${cid}` }}
                  onRemove={(id) => onChange(value.filter((v) => v !== id))}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {!loading && dict == null && (
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-600">
          개념 사전을 불러오지 못했습니다.
          {typeof onRetry === 'function' && (
            <button type="button" onClick={onRetry} className="underline hover:text-amber-700">다시 시도</button>
          )}
        </p>
      )}

      {open && (
        // z-20 — 이 입력은 검색 열 머리(shrink-0)에 있고 결과 목록은 그 다음 형제라 나중에
        // 그려진다. 층을 주지 않으면 후보가 목록 카드 **뒤로** 들어간다.
        <div className="absolute inset-x-0 z-20 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md">
          {matches.length === 0 ? (
            <div className="p-2 text-xs text-gray-400">찾는 개념이 없습니다</div>
          ) : matches.map((c) => (
            <button
              key={c.id}
              type="button"
              // mousedown 을 막지 않으면 입력의 blur 가 먼저 나서 이 목록이 사라지고 click 이 죽는다
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(c.id)}
              className="flex w-full items-center gap-1.5 px-2 py-1 text-left transition-colors hover:bg-primary/10"
            >
              <ConceptChip concept={c} />
              <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">#{c.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ConceptFilterInput;
